import { Sun, Monitor, Moon, type LucideIcon } from 'lucide-react'
import { useTheme, type ThemeMode } from '../theme/ThemeContext.tsx'

const OPTIONS: { mode: ThemeMode; label: string; icon: LucideIcon }[] = [
  { mode: 'light', label: 'Light', icon: Sun },
  { mode: 'system', label: 'System', icon: Monitor },
  { mode: 'dark', label: 'Dark', icon: Moon },
]

/**
 * Light / System / Dark segmented control. `System` follows the OS theme.
 *
 * Collapsed it shows one icon — the current mode. Hovering (or tabbing into it)
 * unfurls it leftward into the full three-way control: the pill widens while the
 * track slides back to its origin, so the active icon glides into position and
 * the other two are revealed from behind the clip.
 *
 * The active slot is a liquid blob that slides between positions — a lead blob
 * plus a lagging trail blob, merged by the SVG goo filter, so the selection
 * stretches and reforms like dragged water.
 *
 * `expanded` pins it open, for contexts where it's the subject rather than a
 * compact affordance (the settings page). Devices without hover get the same,
 * via `@media (hover: none)` — there is no gesture there to unfurl it.
 */
export function ThemeToggle({ expanded = false }: { expanded?: boolean }) {
  const { mode, setMode } = useTheme()
  const activeIndex = Math.max(
    0,
    OPTIONS.findIndex((o) => o.mode === mode),
  )

  return (
    <div
      className={expanded ? 'theme-toggle theme-toggle--open' : 'theme-toggle'}
      role="group"
      aria-label="Colour theme"
      style={{ ['--i' as string]: activeIndex }}
    >
      <div className="theme-toggle__track">
        {/* Gooey filter — merges the two moving blobs into one liquid mass. */}
        <svg className="theme-toggle__defs" aria-hidden="true" focusable="false">
          <filter id="theme-liquid" x="-40%" y="-40%" width="180%" height="180%">
            <feGaussianBlur in="SourceGraphic" stdDeviation="3.2" result="blur" />
            <feColorMatrix
              in="blur"
              type="matrix"
              values="1 0 0 0 0  0 1 0 0 0  0 0 1 0 0  0 0 0 20 -8"
            />
          </filter>
        </svg>

        <span className="theme-toggle__fluid" aria-hidden="true">
          <span className="theme-toggle__blob theme-toggle__blob--trail" />
          <span className="theme-toggle__blob theme-toggle__blob--lead" />
        </span>

        {OPTIONS.map(({ mode: m, label, icon: Glyph }) => (
          <button
            key={m}
            type="button"
            className={`theme-toggle__opt${mode === m ? ' is-active' : ''}`}
            aria-pressed={mode === m}
            title={`${label} theme`}
            onClick={() => setMode(m)}
          >
            <Glyph size={17} strokeWidth={mode === m ? 2.5 : 2} aria-hidden="true" />
            <span className="sr-only">{label} theme</span>
          </button>
        ))}
      </div>
    </div>
  )
}
